import React, { useState } from "react";
import { ArrowUpRight, Mail, Linkedin, Instagram, Send, Calendar, Briefcase, GraduationCap, Sparkles, FileText, ExternalLink, Film } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";
import { sendContact, fileUrl } from "../lib/api";
import VideoPlayer from "./VideoPlayer";
import TitleWithEmphasis from "./TitleWithEmphasis";

const SectionHeading = ({ eyebrow, title, lede }) => (
  <div className="max-w-3xl reveal">
    <div className="flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase text-[#1A1714]/60">
      <span className="inline-block w-8 h-px bg-[#1A1714]/40" />
      {eyebrow}
    </div>
    <h2 className="mt-5 font-serif-display tracking-[-0.01em] leading-[1.02] text-4xl md:text-6xl text-[#1A1714]">
      {title}
    </h2>
    {lede && (
      <p className="mt-5 text-[#1A1714]/75 text-lg leading-[1.7] font-sans-body max-w-2xl">{lede}</p>
    )}
  </div>
);

const About = ({ profile, copy }) => (
  <section id="about" className="py-20 md:py-28">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
      <div className="md:col-span-5 reveal">
        <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-[#EFE8DA]">
          {profile.portrait && (
            <img
              src={fileUrl(profile.portrait)}
              alt="Portrait"
              className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-[1200ms] ease-out"
            />
          )}
          <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[11px] tracking-[0.2em] uppercase text-[#F6F1E8] mix-blend-difference">
            <span>{copy?.portraitCaption || ""}</span>
            <span>{copy?.portraitFigure || "Fig. 01"}</span>
          </div>
        </div>
      </div>
      <div className="md:col-span-7 md:pl-6 lg:pl-10">
        <SectionHeading
          eyebrow={copy?.eyebrow || "About"}
          title={<TitleWithEmphasis text={copy?.title || "Curious by training, commercial by instinct."} emphasis={copy?.titleEmphasis || "commercial"} />}
        />
        <p className="reveal mt-8 text-[#1A1714]/80 text-lg leading-[1.85] font-sans-body whitespace-pre-line">
          {profile.summary}
        </p>
      </div>
    </div>
  </section>
);

const Projects = ({ projects, copy }) => {
  const [active, setActive] = useState(null);
  if (!projects?.length) return null;
  return (
    <section id="work" className="py-20 md:py-28 border-t border-[#1A1714]/12">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionHeading
          eyebrow={copy?.eyebrow || "Selected Work"}
          title={<TitleWithEmphasis text={copy?.title || "Campaigns, case studies & concepts."} emphasis={copy?.titleEmphasis || "case studies"} />}
          lede={copy?.lede || "A short reading list of academic, internship and freelance projects — shown as editorial chapters rather than a portfolio grid."}
        />
        <div className="mt-14 divide-y divide-[#1A1714]/12 border-y border-[#1A1714]/12">
          {projects.map((p, idx) => (
            <article
              key={p.id}
              onMouseEnter={() => setActive(p.id)}
              onMouseLeave={() => setActive(null)}
              className="group relative reveal"
            >
              <a href={`#project-${p.id}`} className="grid grid-cols-12 items-center gap-6 py-8 md:py-10 transition-colors">
                <div className="col-span-1 text-[12px] tracking-[0.2em] uppercase text-[#1A1714]/50">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="col-span-11 md:col-span-7">
                  <h3 className="font-serif-display text-2xl md:text-4xl leading-[1.1] tracking-[-0.01em] text-[#1A1714] group-hover:text-[#B5523A] transition-colors">
                    {p.title}
                  </h3>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] tracking-[0.18em] uppercase text-[#1A1714]/60">
                    {p.client && <span>{p.client}</span>}
                    {p.year && (<><span className="w-1 h-1 rounded-full bg-[#1A1714]/30" /><span>{p.year}</span></>)}
                    {p.tags?.length ? (<><span className="w-1 h-1 rounded-full bg-[#1A1714]/30" /><span>{p.tags.join(" · ")}</span></>) : null}
                  </div>
                </div>
                <div className="hidden md:flex col-span-3 justify-end pr-6">
                  <div className="flex gap-6">
                    {(p.metrics || []).slice(0, 2).map((m, i) => (
                      <div key={i} className="text-right">
                        <div className="font-serif-display text-2xl text-[#1A1714]">{m.value}</div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-[#1A1714]/55">{m.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-1 flex md:justify-end">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#1A1714]/25 group-hover:bg-[#1A1714] group-hover:text-[#F6F1E8] group-hover:border-[#1A1714] transition-all duration-300">
                    <ArrowUpRight size={16} />
                  </span>
                </div>
              </a>

              {p.cover && (
                <div
                  className={`pointer-events-none hidden md:block absolute right-24 top-1/2 -translate-y-1/2 w-[360px] aspect-[4/3] overflow-hidden rounded-sm shadow-2xl shadow-[#1A1714]/30 transition-all duration-500 ${
                    active === p.id ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
                  }`}
                >
                  <img src={fileUrl(p.cover)} alt={p.title} className="w-full h-full object-cover" />
                </div>
              )}

              {/* Expanded detail block */}
              {active === p.id && (
                <div id={`project-${p.id}`} className="pb-10 -mt-2 grid grid-cols-12 gap-6">
                  <div className="col-start-2 col-span-10 md:col-start-2 md:col-span-9">
                    {p.summary && (
                      <p className="text-[#1A1714]/80 text-[15px] leading-[1.8] max-w-2xl">{p.summary}</p>
                    )}
                    {(p.videos?.length) ? (
                      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {p.videos.map((v, i) => (
                          <VideoPlayer key={i} video={v} />
                        ))}
                      </div>
                    ) : null}
                    {(p.gallery?.length || p.documents?.length || p.docs_link) ? (
                      <div className="mt-5 flex flex-wrap gap-3">
                        {(p.documents || []).map((d, i) => (
                          <a
                            key={i}
                            href={fileUrl(d.url)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase border border-[#1A1714]/25 px-3 py-2 rounded-sm hover:bg-[#1A1714] hover:text-[#F6F1E8] transition-colors"
                          >
                            <FileText size={14} /> {d.filename || "Document"}
                          </a>
                        ))}
                        {p.docs_link && (
                          <a
                            href={p.docs_link}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase border border-[#1A1714]/25 px-3 py-2 rounded-sm hover:bg-[#1A1714] hover:text-[#F6F1E8] transition-colors"
                          >
                            <ExternalLink size={14} /> Open Google Doc
                          </a>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>

        {/* Gallery row */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {projects.slice(0, 3).map((p) =>
            p.cover ? (
              <div key={p.id} className="reveal group">
                <div className="relative overflow-hidden aspect-[4/5] rounded-sm bg-[#EFE8DA]">
                  <img
                    src={fileUrl(p.cover)}
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714]/55 via-transparent to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 text-[#F6F1E8]">
                    <div className="text-[11px] tracking-[0.22em] uppercase opacity-80">{p.tags?.[0]}</div>
                    <div className="mt-1 font-serif-display text-xl leading-tight">{p.title}</div>
                  </div>
                </div>
              </div>
            ) : null
          )}
        </div>
      </div>
    </section>
  );
};

const Skills = ({ skills, copy }) => {
  if (!skills?.length) return null;
  return (
    <section id="skills" className="py-20 md:py-28 border-t border-[#1A1714]/12 bg-[#EFE8DA]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionHeading
          eyebrow={copy?.eyebrow || "Capabilities"}
          title={<TitleWithEmphasis text={copy?.title || "A toolkit built between the spreadsheet and the moodboard."} emphasis={copy?.titleEmphasis || "spreadsheet"} />}
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#1A1714]/12">
          {skills.map((s, i) => (
            <div key={i} className="reveal bg-[#F6F1E8] p-8 md:p-10">
              <div className="flex items-center gap-3">
                <Sparkles size={16} className="text-[#B5523A]" />
                <span className="text-[11px] tracking-[0.22em] uppercase text-[#1A1714]/60">
                  {String(i + 1).padStart(2, "0")} · {s.group}
                </span>
              </div>
              <ul className="mt-6 space-y-3">
                {(s.items || []).map((it, j) => (
                  <li key={j} className="font-serif-display text-xl md:text-2xl leading-snug text-[#1A1714]">
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Experience = ({ experience, copy }) => {
  if (!experience?.length) return null;
  return (
    <section id="experience" className="py-20 md:py-28 border-t border-[#1A1714]/12">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionHeading
          eyebrow={copy?.eyebrow || "Experience"}
          title={<TitleWithEmphasis text={copy?.title || "Where I've been learning the craft."} emphasis={copy?.titleEmphasis || "learning the craft"} />}
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-3 reveal">
            <div className="sticky top-28 hidden md:block text-[11px] tracking-[0.22em] uppercase text-[#1A1714]/55">
              <Briefcase size={16} className="text-[#B5523A] mb-3" />
              {copy?.asideLabel || "Roles & collaborations"}
            </div>
          </div>
          <div className="md:col-span-9">
            <ol className="relative">
              {experience.map((x, i) => (
                <li key={i} className="reveal relative pl-8 md:pl-10 pb-10 md:pb-14 border-l border-[#1A1714]/15 last:pb-0">
                  <span className="absolute -left-[7px] top-1 w-[13px] h-[13px] rounded-full bg-[#F6F1E8] border-2 border-[#B5523A]" />
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="font-serif-display text-2xl md:text-3xl text-[#1A1714]">
                      {x.role} <span className="text-[#1A1714]/55">— {x.company}</span>
                    </h3>
                    {x.period && (
                      <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#1A1714]/55">
                        <Calendar size={12} /> {x.period}
                      </div>
                    )}
                  </div>
                  {x.location && (
                    <div className="mt-1 text-[12px] tracking-[0.18em] uppercase text-[#B5523A]/90">{x.location}</div>
                  )}
                  <ul className="mt-5 space-y-2.5 text-[#1A1714]/80 leading-[1.7] text-[15px] max-w-2xl">
                    {(x.bullets || []).map((b, j) => (
                      <li key={j} className="flex gap-3">
                        <span className="mt-2 inline-block w-1.5 h-1.5 shrink-0 bg-[#1A1714]/50" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

const Education = ({ education, copy }) => {
  if (!education?.length) return null;
  return (
    <section id="education" className="py-20 md:py-28 border-t border-[#1A1714]/12 bg-[#EFE8DA]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionHeading
          eyebrow={copy?.eyebrow || "Education"}
          title={<TitleWithEmphasis text={copy?.title || "Formal study, supplemented with practical certificates."} emphasis={copy?.titleEmphasis || "practical certificates"} />}
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {education.map((e, i) => (
            <div key={i} className="reveal bg-[#F6F1E8] p-8 md:p-10 border border-[#1A1714]/12 hover:border-[#1A1714]/35 transition-colors">
              <GraduationCap size={20} className="text-[#B5523A]" />
              {e.period && <div className="mt-5 text-[11px] tracking-[0.22em] uppercase text-[#1A1714]/55">{e.period}</div>}
              <h3 className="mt-2 font-serif-display text-2xl md:text-[26px] leading-[1.15] text-[#1A1714]">{e.school}</h3>
              {e.degree && <div className="mt-3 text-[#1A1714]/85 italic font-serif-display">{e.degree}</div>}
              {e.detail && <p className="mt-4 text-[14px] leading-[1.7] text-[#1A1714]/70">{e.detail}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Contact = ({ profile, copy }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please fill all fields", description: "Name, email and a short message are required." });
      return;
    }
    setSending(true);
    try {
      await sendContact(form);
      setForm({ name: "", email: "", message: "" });
      toast({
        title: copy?.successTitle || "Message sent",
        description: copy?.successDescription || "Thanks for reaching out — I'll get back to you shortly.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Please try again in a moment.";
      toast({ title: "Could not send message", description: typeof detail === "string" ? detail : "Validation failed.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 border-t border-[#1A1714]/12">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
        <div className="md:col-span-6">
          <SectionHeading
            eyebrow={copy?.eyebrow || "Contact"}
            title={<TitleWithEmphasis text={copy?.title || "Let's talk about what's next."} emphasis={copy?.titleEmphasis || "what's next"} />}
            lede={copy?.lede || "Open to graduate roles, internships, freelance briefs and short collaborations — especially in brand, content and consumer insight."}
          />
          <div className="reveal mt-10 space-y-4">
            {profile.email && (
              <a href={`mailto:${profile.email}`} className="flex items-center gap-4 group">
                <span className="inline-flex w-11 h-11 items-center justify-center rounded-full border border-[#1A1714]/25 group-hover:bg-[#1A1714] group-hover:text-[#F6F1E8] transition-colors">
                  <Mail size={16} />
                </span>
                <span className="font-serif-display text-xl md:text-2xl link-underline">{profile.email}</span>
              </a>
            )}
            {profile.linkedin && (
              <a href={profile.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-4 group">
                <span className="inline-flex w-11 h-11 items-center justify-center rounded-full border border-[#1A1714]/25 group-hover:bg-[#1A1714] group-hover:text-[#F6F1E8] transition-colors">
                  <Linkedin size={16} />
                </span>
                <span className="font-serif-display text-xl md:text-2xl link-underline">
                  {profile.linkedin.replace(/^https?:\/\//, "")}
                </span>
              </a>
            )}
            {profile.instagram && (
              <a href={profile.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-4 group">
                <span className="inline-flex w-11 h-11 items-center justify-center rounded-full border border-[#1A1714]/25 group-hover:bg-[#1A1714] group-hover:text-[#F6F1E8] transition-colors">
                  <Instagram size={16} />
                </span>
                <span className="font-serif-display text-xl md:text-2xl link-underline">
                  @{profile.instagram.split("/").filter(Boolean).pop()}
                </span>
              </a>
            )}
          </div>
        </div>

        <form onSubmit={onSubmit} className="md:col-span-6 reveal bg-[#EFE8DA] border border-[#1A1714]/12 p-8 md:p-10 rounded-sm">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#1A1714]/60 mb-6">{copy?.formTitle || "Send a note"}</div>
          <div className="space-y-5">
            <div>
              <label className="text-[11px] tracking-[0.2em] uppercase text-[#1A1714]/60">Name</label>
              <Input
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Your full name"
                className="mt-2 bg-transparent border-0 border-b border-[#1A1714]/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#1A1714] text-lg font-serif-display"
              />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.2em] uppercase text-[#1A1714]/60">Email</label>
              <Input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@company.com"
                className="mt-2 bg-transparent border-0 border-b border-[#1A1714]/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#1A1714] text-lg font-serif-display"
              />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.2em] uppercase text-[#1A1714]/60">Message</label>
              <Textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={5}
                placeholder="A few lines about the role or project…"
                className="mt-2 bg-transparent border-0 border-b border-[#1A1714]/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#1A1714] text-base font-sans-body resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="mt-2 inline-flex items-center gap-2 bg-[#1A1714] text-[#F6F1E8] px-6 py-3 rounded-sm text-[13px] tracking-[0.18em] uppercase hover:bg-[#2c2620] disabled:opacity-60 transition-colors"
            >
              {sending ? "Sending…" : "Send message"} <Send size={14} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const Showreel = ({ showreel, copy }) => {
  if (!showreel?.length) return null;
  return (
    <section id="showreel" className="py-20 md:py-28 border-t border-[#1A1714]/12 bg-[#1A1714] text-[#F6F1E8]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-3xl reveal">
          <div className="flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase text-[#F6F1E8]/60">
            <span className="inline-block w-8 h-px bg-[#F6F1E8]/40" />
            {copy?.eyebrow || "Showreel"}
          </div>
          <h2 className="mt-5 font-serif-display tracking-[-0.01em] leading-[1.02] text-4xl md:text-6xl">
            <TitleWithEmphasis
              text={copy?.title || "Selected film & video work."}
              emphasis={copy?.titleEmphasis || "video"}
              emphasisClass="italic text-[#E8B79A]"
            />
          </h2>
          <p className="mt-5 text-[#F6F1E8]/70 text-lg leading-[1.7] max-w-2xl">
            {copy?.lede || "Short cuts from campaigns, interviews and concept reels — click any tile to play."}
          </p>
        </div>
        <div className={`mt-12 grid gap-5 ${showreel.length === 1 ? "grid-cols-1 max-w-3xl mx-auto" : "grid-cols-1 md:grid-cols-2"}`}>
          {showreel.map((v, i) => (
            <div key={i} className="reveal">
              <VideoPlayer video={v} />
              {v.title && (
                <div className="mt-3 font-serif-display italic text-lg text-[#F6F1E8]/85">{v.title}</div>
              )}
              {v.caption && (
                <div className="mt-1 text-[12px] tracking-[0.16em] uppercase text-[#F6F1E8]/55">{v.caption}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Sections({ content, projects, profile, copy = {} }) {
  return (
    <>
      <About profile={profile} copy={copy.about} />
      <Projects projects={projects} copy={copy.work} />
      <Showreel showreel={content.showreel} copy={copy.showreel} />
      <Skills skills={content.skills} copy={copy.skills} />
      <Experience experience={content.experience} copy={copy.experience} />
      <Education education={content.education} copy={copy.education} />
      <Contact profile={profile} copy={copy.contact} />
    </>
  );
}
